import { Component, OnInit, Injector, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

// components
import { DxPopupComponent } from 'devextreme-angular';
import { ItemDetailsComponent } from '../../shared/components/item-details/item-details.component';
import { ConfirmationComponent } from '../../shared/components/confirmation/confirmation.component';

// models
import { Auction } from '../../shared/models/auction';
import { BuyerCompareNumber } from '../../shared/models/buyer';

import { GallerySeat } from '../shared/models/gallery-seat';
import { AuctionClusterBuyer } from '../shared/models/auction-cluster-buyer';

// services
import { LanguageService } from '../../shared/services/language.service';

import { GalleryService } from '../shared/services/gallery.service';
import { Clock } from '../../shared/models/clock';
import DataSource from 'devextreme/data/data_source';


@Component({
  selector: 'gallery-seat-component',
  templateUrl: 'gallery-seat.component.html',
  styleUrls: ['./gallery-seat.component.scss']
})
export class GallerySeatComponent extends ItemDetailsComponent<GallerySeat> implements OnInit, OnDestroy {

  @ViewChild('confirmation') confirmation: ConfirmationComponent;

  auctionId: number;
  auctions: Array<Auction> = [];
  allBuyers: Array<AuctionClusterBuyer> = [];
  buyers: Array<AuctionClusterBuyer> = [];
  buyerSource: DataSource;
  reservationOnly: boolean;
  selectedClocks: Array<Clock> = [];

  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;

  @ViewChild('name', { read: ElementRef }) nameInput: any;
  @ViewChild(DxPopupComponent) popup: DxPopupComponent;

  constructor(
    protected injector: Injector,
    private dataService: GalleryService,
    private languageService: LanguageService
  ) {
    super(injector);
    this._subscription = this.languageService.direction.subscribe(dir => {
      this.rtlEnabled = dir;
    });
  }

  ngOnInit() {
    this.model = new GallerySeat();
    this.model.reserved = false;
    this.setTranslations('GALLERY');
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  open(gallerySeats: Array<GallerySeat>, gallerySeatId: number, auctionId: number, auctions: Array<Auction>, buyers: Array<AuctionClusterBuyer>, seatNumber: number = null, buyerId: number = null, selectedClocks: Array<Clock> = null) {
    this.allItems = gallerySeats;
    this.auctionId = auctionId;
    this.auctions = auctions;
    this.allBuyers = buyers;
    this.reservationOnly = seatNumber != null && seatNumber > 0;
    this.selectedClocks = selectedClocks;
    this.filterBuyers(auctionId);

    if (gallerySeatId != null) {
      // existing seat
      this.isEditMode = true;
      this.spinner.show();
      this.dataService.getGallerySeat(this.auctionId, gallerySeatId)
        .subscribe((res: GallerySeat) => {
          this.model = res;
          this.isOpened = true;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
    }
    else if (seatNumber != null) {
      // autogenerated seat that wasn't saved yet
      this.model = gallerySeats.find(_ => _.seatNumber === seatNumber);
      this.isOpened = true;
      this.spinner.hide();
    }
    else {
      // new seat
      this.model = new GallerySeat();
      this.model.reserved = false;
      this.model.auctionId = auctionId;
      this.model.buyerId = buyerId;
      this.isEditMode = false;
      this.isOpened = true;
    }
    // setTimeout(() => { this.nameInput.nativeElement.focus(); });
  }

  save() {

    // Check for unique auction-buyer-seat combination
    let existingSeat = this.allItems.find(_ => _.seatNumber === this.model.seatNumber &&
      _.gallerySeatId !== this.model.gallerySeatId &&
      _.auctionId === this.model.auctionId);

    if (existingSeat) {
      this.confirmation.opened = true;
      return;
    }

    
    var buyerDetails = this.allBuyers.find(b => b.buyerId === this.model.buyerId);
    this.model.buyerName = buyerDetails.name;
    this.model.buyerNumber = buyerDetails.buyerNumber;

    //this.model.auctionId = this.auctionId;
    if (this.isEditMode && this.model.gallerySeatId != null) {
      this.spinner.show();
      this.dataService.edit(this.model.auctionId, this.model)
        .subscribe((res: any) => {
          this.model = new GallerySeat();
          this.closeDialog(true);
          //this.errorMessage = null;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
    } else {
      this.spinner.show();
      this.dataService.save(this.model.auctionId, this.model)
        .subscribe((res: any) => {
          this.model = new GallerySeat();
          this.closeDialog(true);
          //this.errorMessage = null;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
    }
  }

  overrideSeat() {
    this.spinner.show();
    this.confirmation.opened = false;

    // Find seat to delete
    let existingSeat = this.allItems.find(_ => _.seatNumber === this.model.seatNumber &&
      _.gallerySeatId !== this.model.gallerySeatId &&
      _.auctionId === this.model.auctionId);

    // Delete seat
    this.dataService.delete(this.model.auctionId, existingSeat.gallerySeatId)
      .subscribe((seats: Array<GallerySeat>) => {
        // Remove deleted seat from existing collection
        this.allItems = this.allItems.filter(_ => _.gallerySeatId !== existingSeat.gallerySeatId);
        this.spinner.hide();

        // Save current seat after deleting previous
        this.save();
      },
        error => {
          this.errorService.show(this.errorService.translations.DELETE_ERROR_MESSAGE);
          this.spinner.hide();
        });
  }

  filterBuyers(auctionId: number) {
    this.buyers = this.allBuyers.filter(b => b.accessToAllAuctionsAndClocks || b.buyerAuctionClocks.some(c => c.auctionId == auctionId));
    this.buyers = this.allBuyers.sort(BuyerCompareNumber);
    this.buyerSource = new DataSource({
      store: this.buyers,
      paginate: true
    });
  }

  onFieldDataChanged(e: any) {
    if (e.component._isReady && e.component.NAME !== 'dxPopup') {
      if (e.dataField == 'auctionId') {
        this.filterBuyers(this.model.auctionId);
      }
      const result = e.component.validate();
      if (result.brokenRules.length >= 1) {
        document.getElementsByName('btnGallerySeatSubmit')[0].setAttribute('disabled', 'disabled');
      } else {
        document.getElementsByName('btnGallerySeatSubmit')[0].removeAttribute('disabled');
      }
    } else {
      if (this.isEditMode) {
        document.getElementsByName('btnGallerySeatSubmit')[0].removeAttribute('disabled');
      }
    }
  }

  public onCancel() {
    this.closeDialog(false);
  }


  closeDialog(dataChanged: boolean) {
    this.model = new GallerySeat();
    this.close(dataChanged);
  };
}
